/**
 * Nest the flat taxonomy node list from `GET /v2/taxonomy/nodes` (linked by
 * `parent_uuid`) into a tree, bottom up: each node is attached to its parent. Nodes
 * stay identified by `uuid`; the backend `key` is the node's slug, which nothing
 * matches collection objects on (see "Object identity" in `@utils/api/collections`),
 * so it is left alone. Nodes whose parent
 * is missing from the list are treated as roots rather than dropped, and so is
 * any node whose parent chain loops back to itself — attaching it would make the
 * tree cyclic, and every recursive walk over it (render, search) would never end.
 */
export const buildObjectTree = (nodes = []) => {
	const byUuid = new Map(nodes.map(node => [node.uuid, { ...node, children: [] }]));
	const parentOf = node => (node.parent_uuid ? byUuid.get(node.parent_uuid) : undefined);

	// Terminates: each step either stops or adds a new node to `visited`, so it
	// takes at most one step per node.
	const isInCycle = start => {
		const visited = new Set();
		for (let current = parentOf(start); current; current = parentOf(current)) {
			if (current === start) return true;
			if (visited.has(current)) return false; // a loop above `start`, not through it
			visited.add(current);
		}
		return false;
	};

	const roots = [];
	byUuid.forEach(node => {
		const parent = parentOf(node);
		if (parent && !isInCycle(node)) parent.children.push(node);
		else roots.push(node);
	});
	return roots;
};
